import type { AnswerValue } from '../dto/survey/survey-answers.types';
import type {
  NodeId,
  NodeIdOrEnd,
  RepeatBlockNode,
  SurveyNode,
  SurveySchema,
} from '../dto/survey/survey-schema.types';

const END = 'END';

/**
 * Defensive cap so a malformed schema with `count: 999999` cannot blow up
 * the progress bar denominator. Schemas with legitimate large counts should
 * fail the validator (see future REPEAT_COUNT_OUT_OF_RANGE rule).
 */
const MAX_ITERATIONS = 1000;

function nextTargets(node: SurveyNode): NodeIdOrEnd[] {
  if (node.type === 'end') {
    return [END];
  }
  const n = (node as Exclude<SurveyNode, { type: 'end' }>).next;
  if (!n) {
    return [END];
  }
  if (n.kind === 'linear') {
    return [n.nextNodeId];
  }
  if (n.kind === 'jump-by-option') {
    return [n.default, ...Object.values(n.perOption)];
  }
  return [n.whenTrue, n.whenFalse];
}

function blockContribution(
  rb: RepeatBlockNode,
  answers?: { byNode: Record<string, AnswerValue> },
): number {
  const innerLen = rb.innerNodes.length;
  let count = 1;
  if (rb.iterationSource.kind === 'fixed-count') {
    count = rb.iterationSource.count;
  } else if (answers) {
    const a = answers.byNode[rb.iterationSource.answerNodeId];
    if (a && a.kind === 'number') {
      count = Math.max(0, Math.floor(a.value));
    }
  }
  return innerLen * Math.min(count, MAX_ITERATIONS);
}

export function computeLongestPath(
  schema: SurveySchema,
  answers?: { byNode: Record<string, AnswerValue> },
): number {
  const byId = new Map(schema.nodes.map((n) => [n.id, n] as const));
  const memo = new Map<NodeId, number>();

  const lp = (id: NodeIdOrEnd): number => {
    if (id === END) {
      return 0;
    }
    if (memo.has(id)) {
      return memo.get(id) as number;
    }
    const node = byId.get(id);
    if (!node) {
      return 0;
    }

    // Mark in-progress to prevent infinite recursion. On a malformed cyclic schema
    // (which the validator rejects for publish), this produces an undercount rather
    // than a crash — acceptable since cycles are only possible on draft schemas
    // being edited live.
    memo.set(id, 0);

    let best = 0;
    if (node.type === 'repeat-block') {
      const inside = blockContribution(node, answers);
      const after = node.next?.kind === 'linear' ? node.next.nextNodeId : END;
      best = inside + lp(after);
    } else {
      for (const t of nextTargets(node)) {
        best = Math.max(best, 1 + lp(t));
      }
    }
    memo.set(id, best);
    return best;
  };

  return lp(schema.startNodeId);
}
